import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UserAvatar } from "./UserAvatar";
import { PosterStripes, PosterGrain, PosterRibbon } from "@/components/PosterFX";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = "image/jpeg,image/jpg,image/png,image/webp,image/gif";

/**
 * Compact reusable dialog for uploading / removing your profile picture.
 * Trigger by setting `open`; the parent controls state.
 */
export default function AvatarUploadDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Reset preview whenever dialog re-opens
  useEffect(() => {
    if (open) setPreview(null);
  }, [open]);

  const pick = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Image too large. Max 5 MB — your file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }
    // Show local preview immediately
    const objUrl = URL.createObjectURL(file);
    setPreview(objUrl);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Profile picture updated");
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e));
      setPreview(null);
    } finally {
      setUploading(false);
      URL.revokeObjectURL(objUrl);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete("/users/me/avatar");
      await refresh();
      toast.success("Profile picture removed");
      setPreview(null);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="text-white max-w-md p-0 border-2"
        style={{
          backgroundColor: "#0A0A0A",
          borderColor: "#178AE5bb",
          backgroundImage: "linear-gradient(160deg, #178AE522, transparent 55%)",
          boxShadow: "0 0 30px #178AE555, inset 0 0 24px rgba(0,0,0,0.55)",
        }}
        data-testid="avatar-upload-dialog"
      >
        <PosterStripes color="#178AE5" size={96} opacity={0.85} />
        <PosterGrain opacity={0.06} />
        <PosterRibbon color="#178AE5" size="sm">★ NEW HEADSHOT ★</PosterRibbon>
        <div className="relative p-4 pt-6 sm:p-5 sm:pt-7">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">PROFILE PICTURE</DialogTitle>
          <DialogDescription className="text-[#A1A1AA] text-sm">
            Square image, up to 5 MB. Cropped to a square and shown on the leaderboard, nav, and (soon) your profile.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 py-2">
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="w-24 h-24 object-cover border-2 border-[#27272A] rounded-sm"
              data-testid="avatar-preview"
            />
          ) : (
            <UserAvatar
              name={user?.name}
              avatarUrl={user?.avatar_url}
              initials={user?.avatar_initials}
              size="xl"
              testid="avatar-current"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#A1A1AA] truncate">{user?.email}</div>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="avatar-file-input"
        />

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            onClick={pick}
            disabled={uploading || removing}
            className="flex-1 bg-[#178AE5] hover:bg-[#41A0E8] text-white font-label rounded-sm h-11"
            data-testid="avatar-upload-button"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading…</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> {user?.avatar_url ? "Replace picture" : "Upload picture"}</>
            )}
          </Button>
          {user?.avatar_url && (
            <Button
              onClick={remove}
              variant="outline"
              disabled={uploading || removing}
              className="border-[#27272A] hover:border-[#E13C32] hover:bg-transparent text-[#A1A1AA] hover:text-[#E13C32] font-label rounded-sm h-11"
              data-testid="avatar-remove-button"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Remove</>}
            </Button>
          )}
        </div>

        <p className="text-[10px] text-[#A1A1AA] mt-1">Accepted: JPEG, PNG, WEBP, GIF. Max 5 MB.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
